import { Alert, Badge, Card, EmptyState, formatBRL, Input, PageHeader, PageLoader, Select, Table, Td, Th, Tr } from '@alicerce/ui';
import { Plus, Upload } from 'lucide-react';
import { Link, useSearchParams } from 'react-router';
import { LoadMore } from '@/components/load-more';
import { errorMessage } from '@/lib/api';
import { useCursorList, useDebounced } from '@/lib/hooks';
import { useCurrent } from '@/lib/session';
import { STATUS_LABEL, STATUS_TONE, type Product } from './types';

const linkButton = 'inline-flex h-10 items-center gap-2 rounded-lg px-4 text-sm font-medium';

export function ProductsListPage() {
  const { can } = useCurrent();
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const q = useDebounced(search);
  const list = useCursorList<Product>(['products'], '/v1/core/products', { q: q || undefined, status: status || undefined, limit: 30 });

  const update = (k: string, v: string) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v);
    else next.delete(k);
    setParams(next, { replace: true });
  };

  return (
    <div>
      <PageHeader
        title="Produtos"
        description="Seu catálogo: o que aparece na loja, nos pedidos, no estoque e nas notas."
        actions={
          can('catalog:write') && (
            <>
              <Link to="/produtos/importar" className={`${linkButton} border border-line bg-surface hover:bg-canvas`}>
                <Upload className="size-4" aria-hidden /> Importar planilha
              </Link>
              <Link to="/produtos/novo" className={`${linkButton} bg-brand-600 text-white hover:bg-brand-700`}>
                <Plus className="size-4" aria-hidden /> Novo produto
              </Link>
            </>
          )
        }
      />
      <Card>
        <div className="flex flex-wrap gap-2 border-b border-line p-3">
          <Input aria-label="Buscar por nome ou SKU" placeholder="Buscar por nome ou SKU" className="max-w-xs" value={search} onChange={(e) => update('q', e.target.value)} />
          <Select aria-label="Situação" className="w-40" value={status} onChange={(e) => update('status', e.target.value)}>
            <option value="">Todas as situações</option>
            <option value="ACTIVE">À venda</option>
            <option value="DRAFT">Rascunho</option>
            <option value="ARCHIVED">Arquivado</option>
          </Select>
          <Link to="/produtos/categorias" className="ml-auto self-center text-sm text-brand-700 hover:underline">
            Categorias
          </Link>
        </div>
        {list.isPending ? (
          <PageLoader />
        ) : list.isError ? (
          <Alert tone="danger" className="m-3">
            {errorMessage(list.error)}
          </Alert>
        ) : list.items.length === 0 ? (
          <EmptyState
            title={q || status ? 'Nenhum produto encontrado' : 'Você ainda não tem produtos'}
            description={q || status ? 'Tente outra busca.' : 'Cadastre o primeiro produto ou importe uma planilha.'}
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Produto</Th>
                  <Th>Situação</Th>
                  <Th className="text-right">Preço</Th>
                  <Th className="text-right">Variantes</Th>
                </tr>
              </thead>
              <tbody>
                {list.items.map((p) => (
                  <Tr key={p.id}>
                    <Td>
                      <Link to={`/produtos/${p.id}`} className="flex items-center gap-3 font-medium hover:underline">
                        {p.images[0] ? (
                          <img src={p.images[0].urls.w400} onError={(e) => (e.currentTarget.src = p.images[0]!.urls.original)} alt="" className="size-10 rounded object-cover" />
                        ) : (
                          <span className="size-10 rounded bg-slate-100" aria-hidden />
                        )}
                        {p.name}
                      </Link>
                    </Td>
                    <Td>
                      <Badge tone={STATUS_TONE[p.status]}>{STATUS_LABEL[p.status]}</Badge>
                    </Td>
                    <Td className="text-right tabular-nums">{formatBRL(p.priceCents)}</Td>
                    <Td className="text-right tabular-nums">{p.hasVariants ? p.variants.length : '—'}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <LoadMore hasNextPage={list.hasNextPage} isFetchingNextPage={list.isFetchingNextPage} fetchNextPage={list.fetchNextPage} />
          </>
        )}
      </Card>
    </div>
  );
}
